import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  Delete,
  Edit,
  Email,
  Language,
  MoreVert,
  Phone,
  Refresh,
  Search,
  Store,
  VpnKey,
} from '@mui/icons-material';
import { resellerService, userService } from '../services/api';
import { ResellerRecord } from '../types';
import PageHeader from '../components/ui/PageHeader';
import SurfaceCard from '../components/ui/SurfaceCard';
import FilterBar from '../components/ui/FilterBar';
import { copyTextToClipboard, promptManualCopy } from '../utils/clipboard';

type NotificationState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
};

type ResellerFormState = {
  displayName: string;
  email: string;
  password: string;
  phone: string;
};

const emptyForm: ResellerFormState = {
  displayName: '',
  email: '',
  password: '',
  phone: '',
};

const formatDateTime = (value?: Date | string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const formatStatusLabel = (active: boolean): string => (active ? 'Attivo' : 'Disattivo');

const ResellersManagementPage: React.FC = () => {
  const [resellers, setResellers] = useState<ResellerRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReseller, setSelectedReseller] = useState<ResellerRecord | null>(null);
  const [detailReseller, setDetailReseller] = useState<ResellerRecord | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState<ResellerFormState>(emptyForm);
  const [notification, setNotification] = useState<NotificationState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadResellers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await resellerService.getResellers();
      setResellers(data);
    } catch (error) {
      console.error('Error loading resellers:', error);
      setNotification({
        open: true,
        message: 'Errore nel caricamento dei reseller',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadResellers();
  }, [loadResellers]);

  const filteredResellers = useMemo(() => {
    return resellers.filter((reseller) => {
      const normalizedSearch = searchTerm.trim().toLowerCase();
      const matchesSearch = !normalizedSearch || [
        reseller.name,
        reseller.email,
        reseller.phone || '',
        reseller.country || '',
        reseller.user?.displayName || '',
      ].some((value) => value.toLowerCase().includes(normalizedSearch));

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && reseller.isActive) ||
        (statusFilter === 'inactive' && !reseller.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [resellers, searchTerm, statusFilter]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, reseller: ResellerRecord) => {
    setAnchorEl(event.currentTarget);
    setSelectedReseller(reseller);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openAddDialog = () => {
    setSelectedReseller(null);
    setFormState(emptyForm);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleEdit = () => {
    if (!selectedReseller) return;
    setFormState({
      displayName: selectedReseller.user?.displayName || selectedReseller.name,
      email: selectedReseller.email,
      password: '',
      phone: selectedReseller.phone || '',
    });
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleViewDetails = async () => {
    if (!selectedReseller) return;
    setDetailDialogOpen(true);
    setDetailLoading(true);
    setGeneratedPassword('');
    handleMenuClose();

    try {
      const reseller = await resellerService.getReseller(selectedReseller.id);
      setDetailReseller(reseller);
    } catch (error) {
      console.error('Error loading reseller details:', error);
      setNotification({
        open: true,
        message: 'Errore nel caricamento del dettaglio reseller',
        severity: 'error',
      });
      setDetailDialogOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = () => {
    setDeleteDialog(true);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (!selectedReseller) return;
    try {
      await resellerService.deleteReseller(selectedReseller.id);
      setNotification({
        open: true,
        message: 'Reseller eliminato con successo',
        severity: 'success',
      });
      setDeleteDialog(false);
      setSelectedReseller(null);
      await loadResellers();
    } catch (error: any) {
      setNotification({
        open: true,
        message: error?.response?.data?.error?.message || 'Errore durante l\'eliminazione del reseller',
        severity: 'error',
      });
    }
  };

  const handleSaveReseller = async () => {
    try {
      if (!formState.email.trim() || !formState.displayName.trim()) {
        setNotification({
          open: true,
          message: 'Compila i campi obbligatori',
          severity: 'error',
        });
        return;
      }

      if (selectedReseller) {
        const payload: { displayName: string; phone: string; password?: string } = {
          displayName: formState.displayName.trim(),
          phone: formState.phone.trim(),
        };

        if (formState.password.trim()) {
          payload.password = formState.password.trim();
        }

        await userService.updateUser(selectedReseller.userId, payload);
        setNotification({
          open: true,
          message: 'Reseller aggiornato con successo',
          severity: 'success',
        });
      } else {
        if (!formState.password.trim()) {
          setNotification({
            open: true,
            message: 'La password iniziale e obbligatoria',
            severity: 'error',
          });
          return;
        }

        await userService.createUser({
          displayName: formState.displayName.trim(),
          email: formState.email.trim(),
          password: formState.password.trim(),
          phone: formState.phone.trim(),
          role: 'RESELLER',
        });

        setNotification({
          open: true,
          message: 'Reseller creato con successo',
          severity: 'success',
        });
      }

      setDialogOpen(false);
      setFormState(emptyForm);
      await loadResellers();
    } catch (error: any) {
      setNotification({
        open: true,
        message: error?.response?.data?.error?.message || 'Errore durante il salvataggio del reseller',
        severity: 'error',
      });
    }
  };

  const copyValue = useCallback(async (text: string, label: string) => {
    const copied = await copyTextToClipboard(text);
    if (copied) {
      setNotification({
        open: true,
        message: `${label} copiato negli appunti`,
        severity: 'success',
      });
      return;
    }

    promptManualCopy(text, label);
    setNotification({
      open: true,
      message: `Clipboard non disponibile: copia manuale ${label.toLowerCase()}`,
      severity: 'warning',
    });
  }, []);

  const handleResetPassword = async () => {
    const target = detailReseller || selectedReseller;
    if (!target) return;
    handleMenuClose();

    try {
      const response = await resellerService.resetPassword(target.id);
      setGeneratedPassword(response.temporaryPassword);
      await copyValue(response.temporaryPassword, 'Password temporanea');

      const refreshed = await resellerService.getReseller(target.id);
      setDetailReseller(refreshed);
      await loadResellers();

      setNotification({
        open: true,
        message: 'Password rigenerata con successo',
        severity: 'success',
      });
    } catch (error: any) {
      setNotification({
        open: true,
        message: error?.response?.data?.error?.message || 'Errore durante la rigenerazione password',
        severity: 'error',
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Gestione Reseller"
        subtitle="Anagrafica commerciale, clienti associati, macchine collegate e credenziali"
        actions={(
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAddDialog}
            sx={{ minWidth: 170 }}
          >
            Aggiungi Reseller
          </Button>
        )}
      />

      <FilterBar>
        <TextField
          placeholder="Cerca per nome, email, telefono, paese..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 320, flexGrow: 1 }}
        />

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Stato</InputLabel>
          <Select
            value={statusFilter}
            label="Stato"
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="all">Tutti</MenuItem>
            <MenuItem value="active">Attivi</MenuItem>
            <MenuItem value="inactive">Disattivi</MenuItem>
          </Select>
        </FormControl>

        <Button startIcon={<Refresh />} onClick={loadResellers}>
          Aggiorna
        </Button>
      </FilterBar>

      <SurfaceCard>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Reseller</TableCell>
                <TableCell>Contatti</TableCell>
                <TableCell>Area</TableCell>
                <TableCell>Clienti</TableCell>
                <TableCell>Macchine</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filteredResellers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 5 }}>
                    <Typography variant="h6" color="text.secondary">
                      Nessun reseller trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredResellers.map((reseller) => (
                  <TableRow key={reseller.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          {reseller.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {reseller.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {reseller.user?.displayName || reseller.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{reseller.email}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {reseller.phone || 'Telefono non impostato'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{reseller.country || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {reseller.region || 'Area non impostata'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${reseller.stats.customers} clienti`}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`${reseller.stats.machines} totali`}
                          color="info"
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${reseller.stats.assignedMachines} assegnate`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatStatusLabel(reseller.isActive)}
                        color={reseller.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(event) => handleMenuClick(event, reseller)} size="small">
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SurfaceCard>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewDetails}>
          <Store sx={{ mr: 1 }} />
          Visualizza dettagli
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <Edit sx={{ mr: 1 }} />
          Modifica
        </MenuItem>
        <MenuItem onClick={handleResetPassword}>
          <VpnKey sx={{ mr: 1 }} />
          Rigenera password
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <Delete sx={{ mr: 1 }} />
          Elimina
        </MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{selectedReseller ? 'Modifica Reseller' : 'Nuovo Reseller'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nome reseller"
              value={formState.displayName}
              onChange={(event) => setFormState((prev) => ({ ...prev, displayName: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formState.email}
              onChange={(event) => setFormState((prev) => ({ ...prev, email: event.target.value }))}
              fullWidth
              required
              disabled={!!selectedReseller}
            />
            <TextField
              label="Telefono"
              value={formState.phone}
              onChange={(event) => setFormState((prev) => ({ ...prev, phone: event.target.value }))}
              fullWidth
            />
            <TextField
              label={selectedReseller ? 'Nuova password (opzionale)' : 'Password iniziale'}
              type="password"
              value={formState.password}
              onChange={(event) => setFormState((prev) => ({ ...prev, password: event.target.value }))}
              fullWidth
              required={!selectedReseller}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleSaveReseller} variant="contained">
            {selectedReseller ? 'Salva modifiche' : 'Crea reseller'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Stai eliminando il reseller "{selectedReseller?.name}". L'operazione e consentita solo se non ha
            clienti o macchine associate.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Annulla</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Dettaglio Reseller</DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detailReseller ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {generatedPassword && (
                <Alert
                  severity="warning"
                  action={(
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={() => copyValue(generatedPassword, 'Password temporanea')}
                    >
                      Copia
                    </Button>
                  )}
                >
                  Password temporanea generata: <strong>{generatedPassword}</strong>
                </Alert>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' }, gap: 2 }}>
                <SurfaceCard sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{detailReseller.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Profilo utente: {detailReseller.user?.displayName || '-'}
                      </Typography>
                    </Box>
                    <Chip
                      label={formatStatusLabel(detailReseller.isActive)}
                      color={detailReseller.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email fontSize="small" color="action" />
                        <Typography variant="body2">{detailReseller.email}</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Telefono</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone fontSize="small" color="action" />
                        <Typography variant="body2">{detailReseller.phone || '-'}</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Paese</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Language fontSize="small" color="action" />
                        <Typography variant="body2">{detailReseller.country || '-'}</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Ultimo login</Typography>
                      <Typography variant="body2">{formatDateTime(detailReseller.user?.lastLoginAt)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Creato il</Typography>
                      <Typography variant="body2">{formatDateTime(detailReseller.createdAt)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Aggiornato il</Typography>
                      <Typography variant="body2">{formatDateTime(detailReseller.updatedAt)}</Typography>
                    </Box>
                  </Box>
                </SurfaceCard>

                <SurfaceCard sx={{ p: 2.5 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                    Sintesi
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Clienti</Typography>
                      <Typography variant="h4">{detailReseller.stats.customers}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Macchine totali</Typography>
                      <Typography variant="h4">{detailReseller.stats.machines}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Macchine assegnate</Typography>
                      <Typography variant="h5">{detailReseller.stats.assignedMachines}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Macchine in stock</Typography>
                      <Typography variant="h5">{detailReseller.stats.inventoryMachines}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      startIcon={<VpnKey />}
                      onClick={handleResetPassword}
                      fullWidth
                    >
                      Rigenera password
                    </Button>
                  </Box>
                </SurfaceCard>
              </Box>

              <SurfaceCard sx={{ p: 0 }}>
                <Box sx={{ p: 2.5, pb: 1 }}>
                  <Typography variant="h6" fontWeight={700}>Clienti associati</Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Telefono</TableCell>
                        <TableCell>Paese</TableCell>
                        <TableCell>Creato il</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailReseller.companies.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                            Nessun cliente associato
                          </TableCell>
                        </TableRow>
                      ) : (
                        detailReseller.companies.map((company) => (
                          <TableRow key={company.id}>
                            <TableCell>{company.name}</TableCell>
                            <TableCell>{company.email}</TableCell>
                            <TableCell>{company.phone || '-'}</TableCell>
                            <TableCell>{company.country || '-'}</TableCell>
                            <TableCell>{formatDateTime(company.createdAt)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SurfaceCard>

              <SurfaceCard sx={{ p: 0 }}>
                <Box sx={{ p: 2.5, pb: 1 }}>
                  <Typography variant="h6" fontWeight={700}>Macchine associate</Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Macchina</TableCell>
                        <TableCell>Modello</TableCell>
                        <TableCell>Seriale</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Stato</TableCell>
                        <TableCell>Posizione</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailReseller.machines.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                            Nessuna macchina associata
                          </TableCell>
                        </TableRow>
                      ) : (
                        detailReseller.machines.map((machine) => (
                          <TableRow key={machine.id}>
                            <TableCell>{machine.name}</TableCell>
                            <TableCell>{machine.model}</TableCell>
                            <TableCell>{machine.serialNumber}</TableCell>
                            <TableCell>{machine.companyName || 'Magazzino reseller'}</TableCell>
                            <TableCell>
                              <Chip
                                label={String(machine.status || '').toUpperCase()}
                                size="small"
                                color={String(machine.status || '').toUpperCase() === 'ONLINE' ? 'success' : 'default'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{machine.location || '-'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SurfaceCard>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3500}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResellersManagementPage;
